import React from 'react';
import { isWeekend, isHoliday } from '../utils/holidays';

const AUTO_G_PER_KM = 170;
const OV_G_PER_KM = 14;
const KCAL_PER_KM_FIETS = 35;

function berekenCo2(year, registrations, userSettings) {
  const fietsKm = parseFloat(userSettings.kmPerRitFiets) || 0;
  const autoKm = parseFloat(userSettings.kmPerRitAuto) || 0;
  const werkdagen = userSettings.werkdagen ?? [1, 2, 3, 4, 5];
  const defaultThuiswerkDagen = userSettings.defaultThuiswerkDagen || [];
  const defaultRegistratieDagen = userSettings.defaultRegistratieDagen || {};

  let fietsGram = 0;
  let ovGram = 0;
  let fietsKcal = 0;

  for (let month = 0; month < 12; month++) {
    const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
    const monthRegs = registrations[monthKey] || {};
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(Date.UTC(year, month, day));
      if (isWeekend(date) || isHoliday(date, year)) continue;
      const dayNum = date.getDay();
      if (!werkdagen.includes(dayNum)) continue;

      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isExplicit = monthRegs[dateString] !== undefined;
      const customDefault = defaultRegistratieDagen[dayNum];
      const defaultDag = customDefault !== undefined ? customDefault : (defaultThuiswerkDagen.includes(dayNum) ? 'thuis' : '');
      const reg = isExplicit ? monthRegs[dateString] : defaultDag;

      if (reg === 'fiets' && fietsKm > 0) {
        fietsGram += fietsKm * 2 * AUTO_G_PER_KM;
        fietsKcal += fietsKm * 2 * KCAL_PER_KM_FIETS;
      }
      if (reg === 'ov' && autoKm > 0) ovGram += autoKm * 2 * (AUTO_G_PER_KM - OV_G_PER_KM);
    }
  }

  return { fietsKg: fietsGram / 1000, ovKg: ovGram / 1000, fietsKcal };
}

function Co2Motivator({ year, registrations, userSettings }) {
  const { fietsKg, ovKg, fietsKcal } = berekenCo2(year, registrations, userSettings);
  const totaalKg = fietsKg + ovKg;

  if (totaalKg < 0.1 && fietsKcal < 1) return null;

  const kmEquivalent = Math.round(totaalKg * 1000 / AUTO_G_PER_KM);
  const bomen = (totaalKg / 22).toFixed(1);

  return (
    <div className="flex-shrink-0 bg-green-50 border-b border-green-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 py-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-green-700">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm font-semibold">
              {totaalKg.toFixed(1)} kg CO₂ bespaard in {year}
            </span>
          </div>

          <div className="flex items-center gap-3 text-xs text-green-600">
            {fietsKg > 0 && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  <circle cx="5" cy="17" r="3" />
                  <circle cx="19" cy="17" r="3" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 17l4-7h6l4 7" />
                </svg>
                fiets {fietsKg.toFixed(1)} kg
              </span>
            )}
            {ovKg > 0 && (
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h8M8 11h4m-7 8l1.5-5H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v5a2 2 0 01-2 2h-1.5L19 19l-5-3-5 3z" />
                </svg>
                OV {ovKg.toFixed(1)} kg
              </span>
            )}
          </div>

          <div className="ml-auto flex items-center gap-4 text-xs text-green-600">
            {fietsKcal >= 1 && (
              <span className="flex items-center gap-1 font-medium text-orange-600" title="Calorieën verbrand door te fietsen (ca. 35 kcal/km)">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                </svg>
                {fietsKcal >= 1000
                  ? `${(fietsKcal / 1000).toFixed(1)} Mcal verbrand`
                  : `${Math.round(fietsKcal).toLocaleString('nl-NL')} kcal verbrand`}
              </span>
            )}
            {totaalKg >= 0.1 && (
              <>
                <span title="Equivalent aan autokilometers">
                  ≈ {kmEquivalent.toLocaleString('nl-NL')} km rijden bespaard
                </span>
                <span title="1 boom absorbeert ca. 22 kg CO₂/jaar">
                  ≈ {bomen} boom{parseFloat(bomen) !== 1 ? 'en' : ''}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Co2Motivator;
